/**
 * Customer storefront / PWA (merchant domain, `/`).
 *
 * Every storefront entry is real (D1–D7): the catalog, identity, cart/checkout/orders,
 * repairs, warranty, buyback, quotes and the seller profile of the merchant this host resolves to.
 */

const crudAction = (model, action, data) => ({ url: `/api/crud/${model}`, method: 'POST', data: { action, data } });
const variantOf = ({ variant_id: id }) => (id == null || id === '' ? {} : { variant_id: id });

export default {
  // ---- real ------------------------------------------------------------
  // Public catalog of the merchant this host resolves to.
  storefront: (body = {}) => ({ url: '/api/crud/Listing', method: 'POST', data: { action: 'storefront', ...body } }),
  // The listing page (WEB-02): filtered, sorted, paged and counted in the query (rule 7, MERCH-86).
  sfSearch: (data = {}) => crudAction('Listing', 'search', data),

  // Digital Inspection Passport of a published unit (WEB-03) → data | null when not inspected yet
  sfInspection: ({ unit_id }) => ({ url: '/api/crud/Listing', method: 'POST', data: { action: 'inspection', data: { unit_id } } }),
  // { units: [{ unit_id }] } → { [unit_id]: { passed, total, report_id } } (WEB-04 compare)
  sfInspectionSummary: ({ units = [] }) => ({
    url: '/api/crud/Listing', method: 'POST', data: { action: 'inspectionSummary', data: { unit_ids: units.map((u) => u.unit_id) } },
  }),

  sfSeller: () => crudAction('Listing', 'seller', {}), // real since D7: the store resolved from the host


  // ---- real (D3): cart, delivery, checkout, payments, orders ---------------
  sfPinCheck: ({ pin }) => crudAction('PinZone', 'check', { pin }),
  // First-party funnel events (D22, storefront/lib/beacon.js); public, fire-and-forget.
  sfTrack: (data) => crudAction('StorefrontEvent', 'track', data),
  // The store's landing page + theme (D24): published, or the draft with a preview token.
  sfAppearance: ({ preview } = {}) => crudAction('StorefrontPage', 'published', preview ? { preview } : {}),
  sfSubscribe: ({ email, consent }) => crudAction('Lead', 'subscribe', { email, consent }),
  sfCart: () => crudAction('Cart', 'get', {}),
  // variant_id (MERCH-87) names the variant of a listing with options; each variant is its own line.
  sfCartAdd: ({ line }) => crudAction('Cart', 'add', { listing_id: line.listing_id, qty: line.qty || 1, ...variantOf(line) }), // → { data: cart, already }
  sfCartSetQty: ({ listing_id, qty, variant_id }) => crudAction('Cart', 'setQty', { listing_id, qty, ...variantOf({ variant_id }) }), // items sold by quantity (D60)
  sfCartRemove: ({ listing_id, variant_id }) => crudAction('Cart', 'remove', { listing_id, ...variantOf({ variant_id }) }),
  sfCartClear: () => crudAction('Cart', 'clear', {}),
  sfCouponApply: ({ code, subtotal_paise }) => crudAction('Coupon', 'apply', { code, subtotal_paise }),
  // Server-priced quote that holds every unit for 15 minutes; 409 when a unit was taken
  sfCheckoutQuote: ({ pin, shipping, coupon }) => crudAction('Checkout', 'quote', { pin, shipping, coupon }),
  // Never sends totals: the server charges the quote it issued. Same key = same order (retry-safe).
  sfOrderPlace: ({ idempotency_key, quote, address, payment_method, upi_id, consent }) =>
    crudAction('SalesOrder', 'place', { idempotency_key, quote: { quote_id: quote && quote.quote_id }, address: { id: address && address.id }, payment_method, upi_id, consent }),
  sfOrders: () => crudAction('SalesOrder', 'mine', {}),
  sfOrder: ({ id }) => crudAction('SalesOrder', 'detail', { id }),
  sfOrderCancel: ({ id, reason }) => crudAction('SalesOrder', 'cancel', { id, reason }),
  sfOrderRequest: ({ id, kind, topic, message }) => crudAction('SalesOrder', 'request', { id, kind, topic, message }),
  sfInvoice: ({ id, kind }) => crudAction('SalesOrder', 'invoice', { id, kind }), // kind: 'credit_note' for the refund's credit note

  // ---- real (D2): customer identity (MP_CUSTOMER session, bound to this store) ----
  sfOtpSend: ({ phone }) => crudAction('Customer', 'requestOtp', { phone }),
  sfOtpVerify: ({ phone, code, request_id, whatsapp_updates }) => crudAction('Customer', 'verifyOtp', { phone, code, request_id, whatsapp_updates }),
  sfSignInMethod: () => crudAction('Customer', 'signInMethod', {}),
  sfOtpVerifyFirebase: ({ id_token, whatsapp_updates }) => crudAction('Customer', 'verifyFirebase', { id_token, whatsapp_updates }),
  sfSession: () => crudAction('Customer', 'session', {}),
  sfLogout: () => crudAction('Customer', 'logout', {}),
  sfAccount: () => crudAction('Customer', 'account', {}),
  sfAssistants: () => crudAction('Customer', 'assistants', {}),
  sfAssistantDisconnect: ({ id }) => crudAction('Customer', 'disconnectAssistant', { id }),
  sfProfileSave: ({ name, email }) => crudAction('Customer', 'updateProfile', { name, email }),
  sfAddressSave: (address) => crudAction('CustomerAddress', 'save', address),
  sfAddressRemove: ({ id }) => crudAction('CustomerAddress', 'remove', { id }),


  // ---- real (D5): repair booking and tracker (WEB-08, WEB-09) ---------------
  // A repair is a service ticket (ST-…). Booking, tracking and deciding need the shopper's sign-in (D11).
  sfRepairSlots: ({ mode } = {}) => crudAction('ServiceTicket', 'slots', { mode }), // pickup | dropoff | return (public)
  sfRepairCreate: (p) => crudAction('ServiceTicket', 'book', p),
  sfRepairs: () => crudAction('ServiceTicket', 'mine', {}),
  sfRepair: ({ id }) => crudAction('ServiceTicket', 'track', { id }),
  sfRepairDecide: (p) => crudAction('ServiceTicket', 'decide', p), // { id, version, decision }
  sfRepairReturn: (p) => crudAction('ServiceTicket', 'scheduleReturn', p), // { id, mode, slot }
  sfRepairPay: (p) => crudAction('ServiceTicket', 'payInvoice', p), // { id, method, upi_id } (D6)
  sfRepairReturnSlots: ({ id, mode }) => crudAction('ServiceTicket', 'returnSlots', { id, mode }), // own repair; delivery | pickup

  // ---- real (D5): warranty (WEB-10) and buyback (WEB-11) --------------------
  // Lookup by serial is public; by order number only for the signed-in buyer. Claims and bookings need sign-in.
  sfWarrantyLookup: ({ query }) => crudAction('Warranty', 'lookup', { query }),
  sfWarrantyClaim: (p) => crudAction('Warranty', 'submitClaim', p),
  sfBuybackEstimate: (p) => crudAction('BuybackEstimate', 'quote', p),
  sfBuybackSlots: () => crudAction('BuybackBooking', 'slots', {}),
  sfBuybackBook: (p) => crudAction('BuybackBooking', 'book', p),
  // ---- real (D7): "Request a quote" → a lead for the store (public, consent-scoped, rate-limited)
  sfEnquiry: (p) => crudAction('Lead', 'enquire', p),
};
